import { useState, type FormEvent } from 'react';
import { MessageSquarePlus, Plus } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { Dialog } from '@/components/dialog';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { DataList, DataListRow } from '@/components/data-list';
import { StatusPill } from '@/components/status-dot';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import { useMe } from '@/lib/auth';
import { usePortalRequests, useCreatePortalRequest } from '@/lib/portal-requests';
import { formatDate, formatStatus } from '@/lib/format';
import type { ClientRequestData } from '@/types/api';

const TYPE_LABELS: Record<string, string> = {
  support: 'Support',
  quote: 'Quote / proposal',
  general: 'General',
};

function statusTone(status: string): 'success' | 'warning' | 'info' | 'neutral' {
  if (status === 'resolved') return 'success';
  if (status === 'in_progress') return 'warning';
  if (status === 'open') return 'info';
  return 'neutral';
}

export function PortalRequests() {
  const { data: paginated, isLoading } = usePortalRequests();
  const rows = paginated?.data;
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState<ClientRequestData | null>(null);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={MessageSquarePlus}
        title="Requests"
        subtitle="Ask for support, request a quote, or send us a message — and track where it stands."
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" /> New request
          </Button>
        }
      />

      {isLoading ? (
        <SkeletonList rows={4} />
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          icon={MessageSquarePlus}
          title="No requests yet"
          description="Need something? Open a request and the Mehrutherm team will pick it up."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New request
            </Button>
          }
        />
      ) : (
        <DataList>
          {rows.map((req) => (
            <DataListRow key={req.id} onClick={() => setViewing(req)} interactive>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium text-foreground">{req.subject}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{TYPE_LABELS[req.type] ?? formatStatus(req.type)}</span>
                  <span aria-hidden>·</span>
                  <span>{formatDate(req.created_at)}</span>
                </div>
              </div>
              <StatusPill tone={statusTone(req.status)}>{formatStatus(req.status)}</StatusPill>
            </DataListRow>
          ))}
        </DataList>
      )}

      <NewRequestDialog open={creating} onClose={() => setCreating(false)} />

      <Dialog open={viewing != null} onClose={() => setViewing(null)} title={viewing?.subject ?? ''}>
        {viewing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-[var(--color-foreground-muted)]">
              <StatusPill tone={statusTone(viewing.status)}>{formatStatus(viewing.status)}</StatusPill>
              <span>{TYPE_LABELS[viewing.type] ?? formatStatus(viewing.type)}</span>
              <span aria-hidden>·</span>
              <span>{formatDate(viewing.created_at)}</span>
            </div>
            <p className="whitespace-pre-line text-sm text-[var(--color-foreground)]">{viewing.body}</p>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function NewRequestDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const clients = me.data?.portal_clients ?? [];
  const create = useCreatePortalRequest();
  const toast = useToast();
  const { error, wrap } = useFormError();

  const [clientId, setClientId] = useState<number | null>(null);
  const [type, setType] = useState('support');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [pending, setPending] = useState(false);

  const resolvedClientId = clientId ?? clients[0]?.id ?? null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!resolvedClientId) return;
    setPending(true);
    const ok = await wrap(() =>
      create.mutateAsync({ client_id: resolvedClientId, type, subject, body }),
    );
    setPending(false);
    if (ok) {
      toast.success('Request submitted', 'The Mehrutherm team has been notified.');
      setSubject('');
      setBody('');
      setType('support');
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New request">
      <form onSubmit={handleSubmit} className="space-y-4">
        {clients.length > 1 ? (
          <Field label="For">
            <Select value={resolvedClientId ?? ''} onChange={(e) => setClientId(Number(e.target.value))}>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="support">Support</option>
            <option value="quote">Quote / proposal</option>
            <option value="general">General</option>
          </Select>
        </Field>
        <Field label="Subject">
          <Input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary"
          />
        </Field>
        <Field label="Details">
          <Textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Tell us what you need…"
            className="min-h-[120px]"
          />
        </Field>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending || !resolvedClientId}>
            {pending ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-[var(--color-foreground-muted)]">{label}</span>
      {children}
    </label>
  );
}
